import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { FaFigma, FaHtml5, FaCss3Alt, FaJs, FaReact, FaNodeJs, FaVuejs, FaAngular } from 'react-icons/fa';
import { SiTailwindcss, SiTypescript, SiNextdotjs, SiGatsby, SiMake } from 'react-icons/si';
import makeIcon from '../assets/logos/make.png';
import newbiLogo from '../assets/logos/newbi.png';

// Outils technologiques avec leurs logos
const techTools = [
  { name: 'Figma', icon: FaFigma, color: '#F24E1E' },
  { name: 'React', icon: FaReact, color: '#61DAFB' },
  { name: 'JavaScript', icon: FaJs, color: '#F7DF1E' },
  { name: 'HTML5', icon: FaHtml5, color: '#E34F26' },
  { name: 'CSS3', icon: FaCss3Alt, color: '#1572B6' },
  { name: 'TailwindCSS', icon: SiTailwindcss, color: '#06B6D4' },
  { name: 'Node.js', icon: FaNodeJs, color: '#339933' },
  { name: 'Vue.js', icon: FaVuejs, color: '#4FC08D' },
  { name: 'Angular', icon: FaAngular, color: '#DD0031' },
  { name: 'TypeScript', icon: SiTypescript, color: '#3178C6' },
  { name: 'Next.js', icon: SiNextdotjs, color: '#000000' },
  { name: 'Gatsby', icon: SiGatsby, color: '#663399' },
];

// Réduire le nombre de logos pour mobile
const mobileLogos = techTools.slice(0, 8);

// Composant pour les logos Make et Newbi qui utilisent des images
const CustomLogoIcon = ({ src, size }) => (
  <img src={src} alt="Logo" style={{ width: size, height: size }} />
);

const FloatingLogosSection = () => {
  const containerRef = useRef(null);

  return (
    <section className="bg-white relative py-16">
      <style>
        {`
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
            100% { transform: translateY(0px); }
          }
          
          .logo-item {
            animation-duration: 3s;
            animation-iteration-count: infinite;
            animation-timing-function: ease-in-out;
            animation-name: float;
          }
          
          .delay-1 { animation-delay: 0s; }
          .delay-2 { animation-delay: 0.5s; }
          .delay-3 { animation-delay: 1s; }
          .delay-4 { animation-delay: 1.5s; }
          .delay-5 { animation-delay: 2s; }
          .delay-6 { animation-delay: 2.5s; }
        `}
      </style>
      <div className="container mx-auto px-4 md:px-8 lg:px-16">
        <div className="flex flex-col lg:flex-row items-center">
          {/* Contenu texte à gauche */}
          <div className="lg:w-1/2 mb-12 lg:mb-0 lg:pr-12">
            <motion.h2 
              className="text-2xl md:text-3xl lg:text-4xl font-medium mb-6 text-[#0a2540] leading-tight max-w-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              Notre stack technologique pour des projets performants
            </motion.h2>
            
            <motion.p 
              className="text-gray-600 mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Chez aiko, nous vous accompagnons dans tous vos besoins digitaux. Notre expertise s'étend de la 
              <span className="text-indigo-600"> création de sites vitrines</span> élégants et performants aux 
              <span className="text-indigo-600"> landing pages</span> optimisées pour la conversion. Nous proposons également des services de 
              <span className="text-indigo-600"> création de logo</span> pour renforcer votre identité visuelle et d'
              <span className="text-indigo-600"> automatisation avec Make</span> pour optimiser vos processus métier.
            </motion.p>
            
            <motion.div
              className="mt-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <a href="#services" className="px-5 py-2 rounded-[40px] bg-[#5046E5] text-white font-medium hover:bg-[#4038c2] transition-all duration-300 shadow-lg text-sm inline-flex items-center">
                Découvrir nos services
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </a>
            </motion.div>
          </div>
          
          {/* Logos à droite - Version desktop */}
          <div className="hidden lg:block lg:w-1/2 relative h-[500px]" ref={containerRef}>
            {/* Logos standards avec icônes */}
            {techTools.map((tool, index) => {
              const Icon = tool.icon;
              
              // Positions fixes et bien espacées pour éviter les chevauchements
              const positions = [
                { top: '5%', left: '10%' },
                { top: '15%', left: '35%' },
                { top: '8%', left: '65%' },
                { top: '25%', left: '85%' },
                { top: '40%', left: '15%' },
                { top: '35%', left: '50%' },
                { top: '45%', left: '75%' },
                { top: '60%', left: '5%' },
                { top: '65%', left: '40%' },
                { top: '70%', left: '65%' },
                { top: '85%', left: '25%' },
                { top: '80%', left: '55%' },
              ];
              
              // Utiliser les positions prédéfinies pour éviter les chevauchements
              const position = positions[index % positions.length];
              
              // Taille variable
              const size = 60 + Math.floor(Math.random() * 20);
              
              return (
                <div 
                  key={`desktop-logo-${index}`} 
                  className={`logo-item delay-${(index % 6) + 1} absolute`}
                  style={{ 
                    top: position.top, 
                    left: position.left,
                    width: `${size}px`,
                    height: `${size}px`,
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    boxShadow: '0 5px 15px 0 rgba(0,0,0,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  }}
                >
                  <Icon size={Math.floor(size * 0.5)} style={{ color: tool.color }} />
                </div>
              );
            })}
            
            {/* Logo Make */}
            <div 
              className="logo-item delay-3 absolute"
              style={{ 
                top: '20%', 
                left: '20%',
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                backgroundColor: 'white',
                boxShadow: '0 5px 15px 0 rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              }}
            >
              <CustomLogoIcon src={makeIcon} size={35} />
            </div>
            
            {/* Logo Newbi */}
            <div 
              className="logo-item delay-5 absolute"
              style={{ 
                top: '75%', 
                left: '45%',
                width: '75px',
                height: '75px',
                borderRadius: '50%',
                backgroundColor: 'white',
                boxShadow: '0 5px 15px 0 rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              }}
            >
              <CustomLogoIcon src={newbiLogo} size={40} />
            </div>
          </div>
          
          {/* Logos à droite - Version mobile (grille fixe) */}
          <div className="lg:hidden w-full">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              {mobileLogos.map((tool, index) => {
                const Icon = tool.icon;
                const size = 50; // Taille fixe sur mobile
                
                return (
                  <div 
                    key={`mobile-logo-${index}`} 
                    className={`logo-item delay-${(index % 6) + 1}`}
                    style={{ 
                      width: `${size}px`,
                      height: `${size}px`,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      boxShadow: '0 5px 15px 0 rgba(0,0,0,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                    }}
                  >
                    <Icon size={Math.floor(size * 0.5)} style={{ color: tool.color }} />
                  </div>
                );
              })}
              
              {/* Make et Newbi sur mobile */}
              <div 
                className="logo-item delay-3"
                style={{ 
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  boxShadow: '0 5px 15px 0 rgba(0,0,0,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                }}
              >
                <CustomLogoIcon src={makeIcon} size={25} />
              </div>
              
              <div 
                className="logo-item delay-5"
                style={{ 
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  boxShadow: '0 5px 15px 0 rgba(0,0,0,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                }}
              >
                <CustomLogoIcon src={newbiLogo} size={25} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FloatingLogosSection;
